/* @flow */
import { default as vendorLoadScript } from 'load-script';
import { MEDIA_QUERY_STRINGS } from '../components/common/media_query';
import zenScroll from 'zenscroll';
import { compose } from 'redux';
import {get, map, each, find, pick, hasIn, invoke, flatten, defer as vendorDefer, throttle as vendorThrottle, sample as vendorSample} from 'lodash';


type LocationAttrs = {
  hostname: string,
  href: string,
  pathname: string
};

type NodeAttrs = {
  clientHeight: number,
  scrollHeight: number,
  scrollTop: number,
  top: number,
  bottom: number
};

type WindowAttrs = {
  innerHeight: number,
  innerWidth: number,
  scrollTop: number
};

/*
 * Note that this is a subset of the opts you can pass into the
 * `load-script` npm package. We're essentially rewriting part of
 * `load-script` for the `loadScriptInDomNode` function.
 */
type LoadScriptInDomNodeOpts = {
  async: bool, attrs: Object
};

/*
 * DOM surrogates
 *
 */

export const reload = (): void =>
  window.location.reload(true);

export const replace = (url: string): void =>
  window.location.replace(url);

export const open = (...args: Array<any>): void =>
  window.open(...args);

export const focus = (domNode: HTMLElement): void =>
  domNode.focus();

export const setInterval = (...args: Array<any>): void =>
  window.setInterval(...args);

export const clearInterval = (...args: Array<any>): void =>
  window.clearInterval(...args);

export const locationAttrs = (window: Object): LocationAttrs =>
  pick(
    window.location,
    ['hostname', 'href', 'pathname', 'protocol', 'search']
  );

export const nodeAttrs = (domNode: HTMLElement): NodeAttrs => ({
  ...pick(domNode, ['clientHeight', 'scrollHeight', 'scrollTop']),
  ...pick(invoke(domNode, 'getBoundingClientRect'), ['top', 'bottom'])
});

export const offsetHeight = (domNode: HTMLElement): number =>
  domNode.offsetHeight;

export const windowAttrs = (): WindowAttrs => ({
  innerHeight: window.innerHeight,
  innerWidth: window.innerWidth,
  scrollTop: get(document, 'body.scrollTop') || get(window, 'pageYOffset')
});

/**
 * Opens a url in a new tab.
 *
 * Because the onClick & target attributes are in conflict this is required for any link utilizing
 * the onClick function.
 */
export const openTab = (location: String): void => window.open(location, '_blank');

export const setLocation = (location: any): string =>
  document.location = location;

export const scrollTo = (...coords: Array<string>): void =>
  window.scrollTo(...coords);

export const scrollBy = (...coords: Array<number>): void =>
  window.scrollBy(...coords);

export const domainCookies = (cookie: string): string =>
  cookie ? document.cookie = cookie : document.cookie;

export const querySelector = (selector: string): ?Object =>
  document.querySelector(selector);

/*
 * DOM helpers
 *
 */

export const componentIsInRangeOfViewport =
  (domNode: HTMLElement, pxRange: number, window: Object): bool => {
    const { top, bottom } = invoke(domNode, 'getBoundingClientRect') || {};
    const ceiling = window.innerHeight + pxRange;
    const floor = 0 - pxRange;
    return top <= ceiling && bottom >= floor;
  };

export const componentIsInRangeOfHorizontalBoundaries =
  (domNode: HTMLElement, pxRange: number, window: Object, leftPos: number, rightPos: number): bool => {
    const { top, bottom, left, right } = invoke(domNode, 'getBoundingClientRect') || {};
    const ceiling = window.innerHeight + pxRange;
    const floor = 0 - pxRange;
    const leftBound = leftPos - pxRange;
    const rightBound = rightPos + pxRange;
    return top <= ceiling && bottom >= floor && left >= leftBound && right <= rightBound;
  };

export const componentIsInHorizontalBox =
  (domNode: HTMLElement, window: Object, leftPos: number, rightPos: number): bool =>
    componentIsInRangeOfHorizontalBoundaries(domNode, 0, window, leftPos, rightPos);

export const componentIsInViewport =
  (domNode: HTMLElement, window: Object): bool =>
    componentIsInRangeOfViewport(domNode, 0, window);

export const documentIsVisible = (document: Object): bool =>
  document.visibilityState === 'visible';

export const getBoundingClientRect = (domNode: HTMLElement): Object =>
  invoke(domNode, 'getBoundingClientRect');

export const nodeYScrollCoord = (domNode: HTMLElement, window: Object): number =>
  get(invoke(domNode, 'getBoundingClientRect'), 'top') + window.scrollY;

export const onHomepage = (location: Object, edition_data: Object): bool =>
  location.pathname === '/' + edition_data.basename;

const getMargin = (node: HTMLElement, window: Object): number =>
  window.getComputedStyle(node).marginRight;

export const getFullWidth = (node: HTMLElement, window: Object): number =>
  hasIn(node, 'offsetWidth')
    ? node.offsetWidth + window.parseInt(getMargin(node, window))
    : 0;

const toArray =
  (htmlCollection: ?HTMLCollection<HTMLAnchorElement>): Array<HTMLAnchorElement> =>
    htmlCollection instanceof HTMLCollection
      ? [].slice.call(htmlCollection)
      : [];

const getChildLinksFromElement =
  (htmlElement: ?HTMLElement): ?HTMLCollection<HTMLAnchorElement> =>
    htmlElement
      ? htmlElement.getElementsByTagName('a')
      : null;

const getChildLinksFromElementAsArray = compose(
  toArray,
  getChildLinksFromElement
);

const getChildLinksFromCollection =
  (htmlCollection: ?HTMLCollection<HTMLElement>): ?Array<HTMLAnchorElement> =>
    map(
      htmlCollection,
      getChildLinksFromElementAsArray,
    );

export const getElementById =
  (id: string, document: Object): ?HTMLElement =>
    document.getElementById(id);

export const getLinksById = compose(
  getChildLinksFromElementAsArray,
  getElementById
);

export const getElementsByClassName =
  (className: string, document: Object): ?HTMLCollection<HTMLElement> =>
    document.getElementsByClassName(className);

export const getLinksByClassName = compose(
  flatten,
  getChildLinksFromCollection,
  getElementsByClassName
);

export const getDomain = (document: Object): bool =>
  document.domain;

export const toggleHtmlNodeClass =
  (document: Object, classToToggle: string): Array<string> =>
    document.getElementsByTagName('html')[0]
      .classList
      .toggle(classToToggle);

export const smoothScrollTo = (node: HTMLElement): void =>
  zenScroll.to(node);

export const smoothScrollToY = (position: number): void =>
  zenScroll.toY(position);

/*
 * Mutates the container's `scrollLeft` so that the child
 * is visible. No-op if the child is already visible.
 */
export const scrollContainerToChildIfNecessary = (
  container: HTMLElement, child: HTMLImageElement, window: WindowAttrs
): void => {
  const { offsetLeft } = child;
  const childPosition = offsetLeft - container.scrollLeft;
  if (childPosition > window.innerWidth || childPosition < 0) {
    container.scrollLeft = offsetLeft;
  }
};

/*
 * Lodash surrogates
 */

export const defer = (...args: Array<any>): Function =>
  vendorDefer(...args);

export const delay = (...args: Array<any>): Function =>
  global.setTimeout(...args);

export const throttle = (...args: Array<any>): Function =>
  vendorThrottle(...args);

export const sample = (...args: Array<any>): Function =>
  vendorSample(...args);

/*
 * Helpers for responsive rendering
 *
 */

export const currentBreakpoint = (): string =>
  find(['mobile', 'tablet', 'desktop'], (breakpoint: string): bool =>
    window.matchMedia(MEDIA_QUERY_STRINGS[breakpoint]).matches
  );

/*
 * load-script helpers
 *
 */

export type CookiebotCategory =
  'necessary' | 'marketing' | 'statistics' | 'preferences' | 'skip';

// Loads a script into the <head> after the 'load' event fires.
export const loadScriptInHead = (category: CookiebotCategory, ...args: Array<any>): void => {
  testableLoadScriptInHead(vendorLoadScript, window, document, category, ...args);
};

/*
 * This pattern is used so that unit tests can stub out `load-script`,
 * `window`, and `document`. Do not import this on prod, import
 * `loadScriptInHead` instead.
 */
export function testableLoadScriptInHead(
  vendorLoadScript: Function,
  window: Object,
  document: Object,
  category: CookiebotCategory,
  ...args: Array<any>
): void {
  deferUntilAfterCookieConsent(
    category,
    window,
    (): void => deferUntilAfterLoadEvent(
      window,
      document,
      (): void => { vendorLoadScript(...args); }
    )
  );
}

/*
 * Loads a script into the <head> "immediately."
 *
 * Immediately means: skips the 'deferred loading' logic, but does not skip
 * Cookiebot prior consent logic.
 *
 * Use sparingly.
 * (Note: this is currently untested!!)
 */
export const loadScriptInHeadImmediately = (
  category: CookiebotCategory, ...args: Array<any>
): void => {
  deferUntilAfterCookieConsent(
    category,
    window,
    (): void => { vendorLoadScript(...args); }
  );
};

// Loads a script into a specified dom node after the 'load' event fires.
export const loadScriptInDomNode = (
  category: CookiebotCategory, src: string, domNode: Object, opts: ?LoadScriptInDomNodeOpts
): void => {
  testableLoadScriptInDomNode(window, document, category, src, domNode, opts);
};

/*
 * This pattern is used so that unit tests can stub out `load-script`,
 * `window`, and `document`. Do not import this on prod, import
 * `loadScriptInDomNode` instead.
 */
export function testableLoadScriptInDomNode(
  window: Object, document: Object, category: CookiebotCategory,
  src: string, domNode: Object, opts: ?LoadScriptInDomNodeOpts
): void {
  deferUntilAfterCookieConsent(
    category,
    window,
    (): void => {
      deferUntilAfterLoadEvent(
        window,
        document,
        (): void => {
          const script = document.createElement('script');
          script.src = src;
          script.type = 'text/javascript';
          script.async = get(opts, 'async', true);
          each(
            get(opts, 'attrs'),
            (value: string, key: string): void => {
              script.setAttribute(key, value);
            }
          );
          domNode.appendChild(script);
        }
      );
    }
  );
}

function deferUntilAfterCookieConsent(
  category: CookiebotCategory,
  window: Object,
  fn: Function
): void {
  /*
   * Short-circuit 1: the script is something we don't want to wrap with
   * Cookiebot (like Cookiebot itself, or DFP). Obviously,
   * we have to load it.
   */
  if (category === 'skip') {
    fn();
    return;
  }

  fn();
}

function deferUntilAfterLoadEvent(
  window: Object,
  document: Object,
  fn: Function
): void {
  if (document.readyState === 'complete') {
    fn();
    return;
  }

  if (window.addEventListener) {
    window.addEventListener('load', (): void => {
      fn();
    });
  } else if (window.attachEvent) { // IE8
    window.attachEvent('onload', (): void => {
      fn();
    });
  }
}

/*
 * Helper used to only load scripts once, into the <head>.
 *
 * Example:
 *
 * loadScriptInHeadOnce("marketing", "www.foo.com/bar.js");
 * loadScriptInHeadOnce("marketing", "www.foo.com/bar.js");
 * // bar.js is only loaded once
 *
 * loadScriptInHeadOnce("necessary", "www.foo.com/hello.js");
 * loadScriptInHeadOnce("necessary", "www.foo.com/world.js");
 * // Both hello.js and world.js are loaded
 *
 * Also, we ensure that the callback is respected either way:
 *
 * loadScriptInHeadOnce("marketing", "www.foo.com/bar.js", () => {
 *   console.log("Bar loaded once");
 * });
 * loadScriptInHeadOnce("marketing", "www.foo.com/bar.js", () => {
 *   console.log("Tried to load bar a second time");
 * });
 *
 * Both log lines should happen.
 *
 * TODO: Figure out if this should just be our global `loadScriptInHead`.
 * After all, is there ever a case where we need to load an external script
 * twice?
 */
export const loadScriptInHeadOnce = (
  category: CookiebotCategory,
  ...args: Array<any>
): void => {
  testableloadScriptInHeadOnce(loadScriptInHead, category, ...args);
};

export const _scriptsLoaded = []; // 👈 only exported so that unit tests can
/*
 * clear it, do not import in prod code!
 * This pattern is used so that unit tests can stub out `loadScriptInHead`. Do
 * not import this on prod, import `loadScriptInHeadOnce` instead.
 */
export function testableloadScriptInHeadOnce(
  loadScriptInHead: Function,
  category: CookiebotCategory,
  ...args: Array<any>
): void {
  /*
   * Preserve the API of the `load-script` npm package we use
   * https://github.com/eldargab/load-script/blob/master/index.js#L2
   */
  const src = args[0];
  let cb;
  if (typeof args[1] === 'function') {
    cb = args[1];
  } else if (typeof args[2] === 'function') {
    cb = args[2];
  }

  if (_scriptsLoaded.includes(src)) {
    // If the script has already been loaded, just call the cb.
    cb && cb();
  } else {
    loadScriptInHead(category, ...args);
    _scriptsLoaded.push(src);
  }
}

/*
 * Currently used in HTMLSections, `loadScriptsInHeadSequentially` takes in an
 * array of <script> DOM elements, whether external or inline,
 * and loads them in order by inserting them into the <head>.
 *
 * For example, consider this example (poorly coded) HTML embed:
 *
 *   <script src="http://www.jquery.com/jquery.js">
 *   <p>Text</p>
 *   <script type="text/javascript">
 *     console.log(window.jQuery);
 *   </script>
 *
 *  We need to make sure that jQuery.js is loaded SYNCHRONOUSLY in a blocking
 *  manner, and that the inline script executes sometime after it.
 *
 *  However, if that <script> tag had an 'async' on it, then it would not have
 *  to be loaded synchronously. This helper accounts for all such cases.
 *
 *  Note: for now, we're considering all of these "necessary"-type scripts
 *  for the sake of Cookiebot consent.
 */
export const loadScriptsInHeadSequentially = (scripts: Array<Object>): void => {
  testableloadScriptsInHeadSequentially(loadScriptInHeadOnce, scripts);
};

/*
 * This pattern is used so that unit tests can stub out `loadScriptInHeadOnce`. Do
 * not import this on prod, import `loadScriptsInHeadSequentially` instead.
 */
export function testableloadScriptsInHeadSequentially(
  loadScriptInHeadOnce: Function,
  scripts: Array<Object>
): void {
  if (scripts.length === 0) {
    return;
  }

  /*
   * Basic idea is to handle the first script, then eventually recursively
   * call the function on the rest of the list.
   */

  const [script, ...rest] = scripts;
  const loadRestOfScripts = (): void =>
    testableloadScriptsInHeadSequentially(loadScriptInHeadOnce, rest);

  // External script
  if (script.src) {
    if (script.async || script.defer) {
      loadScriptInHeadOnce('necessary', script.src);
      loadRestOfScripts();
    } else {
      loadScriptInHeadOnce('necessary', script.src, loadRestOfScripts);
    }

  // Inline script
  } else {
    window.eval(script.innerHTML);
    loadRestOfScripts();
  }
}
